import React from "react"
import { withPrefix } from "gatsby"
import Layout from "../../components/layout"
import Hero, { HeroCaption } from "../../components/hero"
import SectionEnder from "../../components/section-ender"
import { PageColumns, Column } from "../../components/page-column"
import Page from "../../components/page"
import Image from "../../components/image"
import Chart from "../../components/chart"
import { figure_1_6 } from "../../data/charts"

export default function ClimateChange() {
  return (
    <Layout>
      <Hero
        title="Climate Change, Digitalization, and Inclusion"
        text="Major structural transformations are underway. Policymakers should seize the opportunities."
        image="in-focus/climate-change/hero.jpg"
        video={withPrefix("/") + "video/climate-change/hero.mp4?v=1"}
      />

      <Page>
        <PageColumns>
          <Column location="left" classes="util__desktop-only">
            <HeroCaption>
              Major structural transformations are underway. Policymakers should
              seize the opportunities.
            </HeroCaption>
          </Column>
          <Column classes="text-block">
            <p className="dropcap">
              Even as countries battle crises on multiple fronts, it is crucial
              not to overlook the longer-term challenge of improving their
              resilience to shocks and achieving sustainable and inclusive
              growth. If these long-term challenges are not addressed in a
              timely manner, there can be significant economic consequences,
              with the potential for future balance of payments problems.
            </p>

            <h2>Tackling climate change</h2>
            <p>
              Climate change imposes large economic and social costs, in part by
              contributing to a higher frequency and intensity of natural
              disasters, affecting macroeconomic and financial stability. For
              the IMF to live up to its mandate, it needs to assist its members
              in managing these challenges by rapidly scaling up and more
              systematically covering climate-related issues through its lending
              as well as its analytical, surveillance, and capacity development
              work.
            </p>
            <p>
              The Executive Board approved a{" "}
              <a href="https://www.imf.org/en/Publications/Policy-Papers/Issues/2021/07/30/IMF-Strategy-to-Help-Members-Address-Climate-Change-Related-Policy-Challenges-Priorities-463093">
                strategy
              </a>{" "}
              to help members address climate-change-related policy challenges{" "}
              <a href="https://www.imf.org/en/News/Articles/2021/07/30/pr21238-imf-executive-board-discusses-strategy-address-climate-change-related-policy-challenges">
                in July 2021
              </a>
              . As part of the IMF’s surveillance, mitigation and adaptation
              policies and strategies for managing the transition to a
              low-carbon economy—especially for countries heavily dependent on
              fossil fuel production—are now regularly covered during Article IV
              consultations. In the past year, climate issues featured in about
              30 country assessments, including those for Barbados, Canada,
              China, Fiji, Germany, Malawi, Mexico, the United Kingdom, and the
              United States.
            </p>
          </Column>
        </PageColumns>

        <Chart chart={figure_1_6} />

        <PageColumns>
          <Column classes="text-block">
            <p>
              In an effort to integrate in-depth climate-related risk
              assessments into the Fund’s work, the IMF’s Financial Sector
              Assessment Program (FSAP) now incorporates climate risk analysis,
              including stress testing where relevant. Climate risk analysis has
              been completed in regard to Colombia, Norway, the Philippines,
              South Africa, and the United Kingdom. Assessment of supervisory
              frameworks will also start evaluating climate risks.
            </p>
            <p>
              Work is also underway to scale up climate-related capacity
              development. For example, to help governments improve the
              effectiveness of public investment in low-carbon and
              climate-resilient infrastructure, a new climate module has been
              added to the current Public Investment Management Assessment
              (PIMA) framework. The &ldquo;Climate-PIMA&rdquo; has been tested
              in more than 15 countries. A new IMF climate diagnostic tool, the
              Climate Macroeconomic Assessment Program, has been developed and
              piloted in two countries. The tool is intended to assess the
              macro-fiscal risks of climate shocks and stresses, the
              preparedness of climate-vulnerable countries, and the implications
              of mitigation and adaptation policies. A &ldquo;green public
              financial management&rdquo; framework was released in August 2021
              and showcased in several regional trainings, helping governments
              integrate climate into public financial management practices.
            </p>
            <p>
              To improve data and disclosure to more effectively price and
              manage climate risks, in 2021 the IMF launched the{" "}
              <a href="https://climatedata.imf.org/">
                Climate Change Indicators Dashboard
              </a>
              , which has since been further updated. The dashboard builds on
              collaboration with other international organizations and includes
              a range of distinctive indicators that demonstrate the impact of
              economic activity on climate change, making it a one-stop shop for
              relevant climate-change-related macroeconomic data. These
              indicators have been grouped into five categories:{" "}
              <a href="https://climatedata.imf.org/pages/re-indicators">
                Economic Activity
              </a>
              ,{" "}
              <a href="https://climatedata.imf.org/pages/bp-indicators">
                Cross-Border
              </a>
              ,{" "}
              <a href="https://climatedata.imf.org/pages/fi-indicators">
                Financial and Risk
              </a>
              ,{" "}
              <a href="https://climatedata.imf.org/pages/go-indicators">
                Government Policy
              </a>
              , and{" "}
              <a href="https://climatedata.imf.org/pages/climatechange-data">
                Climate Change Data
              </a>
              .
            </p>
            <p>
              IMF staff members also cohost the Secretariat of the Coalition of
              Finance Ministers for Climate Action, as well as the Financial
              Stability Board’s working group on climate risks, data, and
              vulnerabilities. The institution collaborates with international
              organizations such as the Bank for International Settlements;
              Network for Greening the Financial System, where IMF staff members
              cochair the &ldquo;bridging the data gaps&rdquo; workstream;
              Organisation for Economic Co-operation and Development; United
              Nations; and World Bank.
            </p>
          </Column>
        </PageColumns>

        <PageColumns modifiers="page-columns--spacing-medium" media>
          <Column location="left">
            <Image
              path="in-focus/climate-change/climate-change-1.jpg"
              alt="Person using a mobile phone to scan a digital payment code in Gurgaon, India"
              caption="India"
            />
          </Column>
        </PageColumns>

        <PageColumns>
          <Column classes="text-block">
            <h2>Digitalization</h2>
            <p>
              Digital forms of money are diverse and evolving rapidly. The
              opportunities are immense, but the challenges to policymakers are
              also stark, complex, and widespread. The most far-reaching
              implications are to the stability of the international monetary
              system. Digital money must be designed and regulated so that
              member countries reap the potential benefits, including greater
              financial inclusion and more efficient payments across borders.
              Achieving these goals requires managing risks related to capital
              flow volatility and loss of control over monetary policy.
              International cooperation will be key to mitigating cross-border
              spillovers.
            </p>
            <p>
              The IMF has a mandate to help ensure that widespread adoption of
              new forms of digital money fosters domestic economic and financial
              stability, as well as the stability of the international monetary
              system, and is engaging regularly with authorities to evaluate
              country-specific policies, identify policy options and trade-offs,
              and provide capacity development.
            </p>
            <p>
              To do so, the IMF is deepening its expertise and collaborating
              closely with the Bank for International Settlements, Financial
              Stability Board, World Bank, and other international working
              groups and standard-setting bodies.
            </p>
            <p>
              As part of the IMF’s surveillance, the broad domestic effects of
              digital money adoption are also being covered in an increasing
              number of countries. For example, the{" "}
              <a href="https://www.imf.org/en/Publications/CR/Issues/2022/05/06/The-Bahamas-2022-Article-IV-Consultation-Press-Release-Staff-Report-and-Statement-by-the-517631">
                recent Article IV consultation on The Bahamas
              </a>{" "}
              included an analysis of the introduction of the &ldquo;Sand
              Dollar&rdquo; (the digital version of the Bahamian dollar).
              Analysis of risks related to digital financial services will also
              be included in FSAP reports, as was done recently for Korea,
              Singapore, and Switzerland. Assessment tools are being upgraded to
              incorporate new sources of risks, as well as guidance and
              recommendations issued by international standard-setting bodies.
            </p>
            <p>
              Digitalization and mobile money are also rapidly transforming
              fiscal operations and policies through{" "}
              <a href="https://www.sarttac.org/content/imf/en/annual-meetings/calendar/open/2022/04/15/164234.html?calendarCategory=T2ZmaWNpYWwvQnkgSW52aXRhdGlvbg==.UHJlc3M=.V29ybGQgQmFuaw==.T3Blbg==">
                GovTech
              </a>
              , an area of increasing IMF support to members. During the
              pandemic, governments accelerated digital delivery of key
              government services. Revenue administrations are increasingly
              using e-tax filing and e-payment systems and digital technologies
              to improve compliance management, helping to reduce tax evasion
              and boost revenue mobilization. Digital technologies are also
              improving the efficiency and effectiveness of public financial
              management systems and processes, including budget preparation and
              execution, cash and debt management, e-procurement, financial
              reporting and audits, and administration of social programs. In
              many countries, digitalization is also enabling improvements in
              governance and fiscal transparency, allowing citizens and other
              stakeholders to have easy access to information on government
              revenues and spending and reducing opportunities for corruption.
            </p>
          </Column>
        </PageColumns>

        <PageColumns modifiers="page-columns--spacing-medium" media>
          <Column location="right">
            <Image
              path="in-focus/climate-change/climate-change-2.jpg"
              alt="Woman on construction site wearing a hard hat"
              caption="Côte d’Ivoire"
            />
          </Column>
        </PageColumns>

        <PageColumns>
          <Column classes="text-block">
            <h2>Inclusive growth and gender</h2>
            <p>
              Inequalities within and across countries widened during the
              pandemic. Spillovers from the war in Ukraine, including the threat
              of fragmentation, are likely to amplify these inequalities,
              potentially rolling back years of progress.
            </p>
            <p>
              The pandemic has also deepened long-standing gender gaps, which
              has macro-critical implications. IMF research has consistently
              underscored the benefits of addressing extreme inequality,
              including greater productivity and financial stability.
            </p>
            <p>
              The IMF finalized a strategy in July 2022 to better integrate work
              on gender in its analysis and advice. The Fund continues to
              deliver hands-on capacity development, particularly on gender
              budgeting, and has started a new collaboration with the Gates
              Foundation to strengthen analysis and advice on gender policies
              and institutions. As part of the{" "}
              <a href="https://www.tax-platform.org/">
                Platform for Collaboration on Tax
              </a>
              —a joint initiative with the Organisation for Economic
              Co-operation and Development, United Nations, and World Bank—the
              IMF is also considering the role of taxation in achieving gender
              equality.
            </p>
            <p>
              In addition to the analytical work underway on income and wealth
              inequality, the IMF continues to implement its strategy for
              engagement on social spending. The{" "}
              <a href="https://www.imf.org/en/Capacity-Development/what-we-do/covid19-crisis-capacity-development-initiative">
                IMF's COVID-19 Crisis Capacity Development Initiative
              </a>{" "}
              is helping low-income countries and emerging market and developing
              economies address inequalities, including by improving tax
              policies and leveraging digitalization to create safety nets and
              accelerate cash transfers.
            </p>
            <p>
              A study is also underway on epidemics, gender, and human capital,
              drawing lessons from previous health crises. The analysis will
              quantify the impact of health crises on school completion rates in
              low-income countries, particularly for girls.
            </p>
          </Column>
        </PageColumns>
      </Page>

      <SectionEnder sectionID="what-we-do" />
    </Layout>
  )
}
